import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Paper, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import QixitAuthPanel from '@shared/auth/QixitAuthPanel';
import { resolveReferralFromContext } from '@shared/auth/attribution';
import type { QixitAuthMeta, QixitAuthSuccess } from '@shared/auth/types';
import { clearStoredReferral } from '@shared/referralAttribution';
import { mailboxAuthApi } from '../services/qixitAuthApi';
import { finishMailboxSignIn, isTrustedReturnUrl } from '../services/finishMailboxSignIn';
import { applyAuthenticatedUser } from '../store/authSlice';
import type { AppDispatch } from '../store/store';

/**
 * Generic "create a Qixit account" page. Links posted on other sites carry
 * ?ref=CODE; that code (or the referral cookie) reaches every signup path.
 * The old multi-step registration form is retired. POST /api/Registration/register
 * and /verify-email remain for accounts created before express signup.
 */
const Register: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl')?.trim() || '';

  const referral = useMemo(() => resolveReferralFromContext(searchParams), [searchParams]);
  const attribution = useMemo(
    () => ({
      referralCode: referral.referralCode,
      sourcePage: referral.fromUrl ? referral.referralSource : 'mailbox-register',
    }),
    [referral]
  );

  const handleAuthenticated = async (result: QixitAuthSuccess, meta: QixitAuthMeta) => {
    const user = await finishMailboxSignIn(result);
    if (meta.isNewAccount) clearStoredReferral();
    dispatch(applyAuthenticatedUser(user));
    if (returnUrl && isTrustedReturnUrl(returnUrl)) {
      if (returnUrl.startsWith('/')) navigate(returnUrl);
      else window.location.assign(returnUrl);
      return;
    }
    navigate(meta.isNewAccount ? '/profile?tab=account-settings' : '/messages');
  };

  return (
    <Container maxWidth="xs" data-testid="register-page">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <QixitAuthPanel
          api={mailboxAuthApi}
          context="mailbox"
          variant="page"
          testIdPrefix="mailbox"
          defaultMode="create"
          title="Create your Qixit account"
          subtitle={
            <>
              New? Enter your email* or continue with Google or Microsoft to create an account.
              {referral.referralCode ? (
                <Typography component="span" sx={{ display: 'block', fontSize: 'inherit' }} data-testid="register-referral-applied">
                  Your referral link has been applied.
                </Typography>
              ) : null}
            </>
          }
          returnUrl={typeof window !== 'undefined' ? window.location.href : undefined}
          attribution={attribution}
          onAuthenticated={handleAuthenticated}
        />
      </Paper>
    </Container>
  );
};

export default Register;
